import React from 'react';

import {
    facilies,
    facilitiesNew,
    tshirtOpt,
    scholarshipOpt,
    travelOpt,
    TRAVEL_NEEDS_INFO,
    SCHOLARSHIP_HAS_URL,
} from '../../../../helper';
import { useProgramForm } from '../../../../context/ProgramFormContext';
import SectionCard from '../ui/SectionCard';
import Field from '../ui/Field';
import { TextInput, TextareaInput, SelectInput } from '../ui/Inputs';

/**
 * Section 6 — Amenities. Ports `AddProgramModal.js:2281-2392`.
 *
 * Two conditional fields, both preserved: the Scholarships URL appears once Scholarships
 * offers something, and Travel Info once Travel involves the camp providing transport.
 *
 * The option lists were originally declared here to keep the legacy `AddProgramModal`'s
 * Yes/No labels untouched. That backfired: the display pages resolve these codes through
 * `helper.js`, knew nothing about the new values, and blank-paged on them. The lists now
 * live in `helper.js` as the single source of truth and are imported here. The legacy
 * modal is unaffected — it reads `facilies` and `trueFalse`, both still intact.
 */

/**
 * Facility's six current options (`facilitiesNew`) have no equivalent for the three legacy
 * codes — 3 in particular ("Both indoor and outdoor") covers most existing programs and
 * cannot be mapped onto a single new value without inventing data. A legacy code is
 * therefore kept as a valid stored value but offered only to a program already holding
 * one, so the list stays clean for everyone else while nothing silently changes underneath.
 */
const LEGACY_FACILITY_VALUES = facilies.map(item => item.value);

/**
 * Meals is stored as an array of individual meals and is sent that way, but the seven
 * choices below are the only combinations offered — they are mutually exclusive, so the
 * control is a single select that maps to and from the array at the edges.
 */
const mealsOpt = [
    { value: 'none', label: 'No meals provided', meals: [] },
    { value: 'breakfast', label: 'Breakfast Only', meals: ['breakfast'] },
    { value: 'lunch', label: 'Lunch only', meals: ['lunch'] },
    { value: 'dinner', label: 'Dinner Only', meals: ['dinner'] },
    { value: 'lunch-dinner', label: 'Lunch and Dinner', meals: ['lunch', 'dinner'] },
    { value: 'breakfast-lunch', label: 'Breakfast & lunch', meals: ['breakfast', 'lunch'] },
    { value: 'all', label: 'All meals included', meals: ['breakfast', 'lunch', 'dinner'] },
];

const sameMeals = (a, b) =>
    a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const Amenities = () => {
    const {
        userInput,
        setUserInput,
        handleChange,
        handleSelect,
        fieldStatus,
        requiredOnly,
    } = useProgramForm();

    // Three meals make eight possible sets but only seven are offered — breakfast with
    // dinner and no lunch has no label. Rather than render an empty box and rewrite the
    // value on the next save, an existing off-list array is shown as its own option.
    const storedMeals = Array.isArray(userInput.meals) ? userInput.meals : null;
    const mealsMatch = storedMeals ? mealsOpt.find(item => sameMeals(item.meals, storedMeals)) : null;
    const mealsLegacy =
        storedMeals && !mealsMatch
            ? { value: storedMeals.join('-'), label: storedMeals.join(', '), meals: storedMeals }
            : null;

    const facilityIsLegacy = LEGACY_FACILITY_VALUES.includes(userInput.facility);
    const facilityOptions = facilityIsLegacy
        ? [...facilitiesNew, ...facilies.filter(item => item.value === userInput.facility)]
        : facilitiesNew;

    return (
        <SectionCard
            id="amenities"
            step={6}
            title="Amenities"
            description="What's provided and what campers bring"
        >
            <div className="row">
                {!requiredOnly ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="Meals"
                        htmlFor="meals"
                        help="Meals included in the program price. Dietary needs are collected at registration."
                        {...fieldStatus('meals')}
                    >
                        <SelectInput
                            id="meals"
                            pHolder="Meals"
                            // `|| []` covers the cleared case: the empty option `ui/Inputs.js`
                            // substitutes for a null carries no `meals` of its own. The payload
                            // already coerces a falsy `meals` to '[]', so this only keeps an
                            // `undefined` out of `userInput`.
                            change={e => setUserInput({ meals: e.meals || [] })}
                            value={mealsMatch || mealsLegacy || ''}
                            options={mealsLegacy ? [...mealsOpt, mealsLegacy] : mealsOpt}
                        />
                    </Field>
                ) : ''}

                <Field
                    className="col-md-6 mb-3"
                    label="T-shirt"
                    htmlFor="tshirt"
                    required
                    {...fieldStatus('tshirt')}
                >
                    <SelectInput
                        id="tshirt"
                        pHolder="T-shirt"
                        change={e => handleSelect(e, 'tshirt')}
                        value={tshirtOpt.filter(el => el.value === userInput.tshirt)}
                        options={tshirtOpt}
                        error={fieldStatus('tshirt').error}
                    />
                </Field>

                <Field
                    className="col-md-6 mb-3"
                    label="Equipment Needed"
                    htmlFor="equipment"
                    required
                    help="Enter ‘None’ if no equipment needs to be brought by the camper."
                    {...fieldStatus('equipment')}
                >
                    <TextInput
                        id="equipment"
                        name="equipment"
                        value={userInput.equipment}
                        change={handleChange}
                        pHolder="Equipment"
                        error={fieldStatus('equipment').error}
                    />
                </Field>

                <Field
                    className="col-md-6 mb-3"
                    label="Facility"
                    htmlFor="facility"
                    required
                    hint={facilityIsLegacy ? 'This program uses an older facility option.' : ''}
                    {...fieldStatus('facility')}
                >
                    <SelectInput
                        id="facility"
                        pHolder="Facility"
                        change={e => handleSelect(e, 'facility')}
                        value={facilityOptions.filter(el => el.value === userInput.facility)}
                        options={facilityOptions}
                        error={fieldStatus('facility').error}
                    />
                </Field>

                <Field
                    className="col-md-6 mb-3"
                    label="Scholarships"
                    htmlFor="scholarships"
                    required
                    {...fieldStatus('scholarships')}
                >
                    <SelectInput
                        id="scholarships"
                        pHolder="Scholarships"
                        change={e => handleSelect(e, 'scholarships')}
                        value={scholarshipOpt.filter(el => el.value === userInput.scholarships)}
                        options={scholarshipOpt}
                        error={fieldStatus('scholarships').error}
                    />
                </Field>

                {SCHOLARSHIP_HAS_URL.includes(userInput.scholarships) ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="Scholarships Url"
                        htmlFor="scholarshipsUrl"
                        hint="Include http:// or https://"
                        {...fieldStatus('scholarshipsUrl')}
                    >
                        <TextInput
                            id="scholarshipsUrl"
                            name="scholarshipsUrl"
                            value={userInput.scholarshipsUrl}
                            change={handleChange}
                            pHolder="Scholarships Url"
                            error={fieldStatus('scholarshipsUrl').error}
                        />
                    </Field>
                ) : ''}

                <Field
                    className="col-md-6 mb-3"
                    label="Travel"
                    htmlFor="travel"
                    required
                    {...fieldStatus('travel')}
                >
                    <SelectInput
                        id="travel"
                        pHolder="Travel"
                        change={e => handleSelect(e, 'travel')}
                        value={travelOpt.filter(el => el.value === userInput.travel)}
                        options={travelOpt}
                        error={fieldStatus('travel').error}
                    />
                </Field>

                {TRAVEL_NEEDS_INFO.includes(userInput.travel) ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="Travel Info"
                        htmlFor="travelInfo"
                        required
                        {...fieldStatus('travelInfo')}
                    >
                        <TextareaInput
                            id="travelInfo"
                            name="travelInfo"
                            value={userInput.travelInfo}
                            change={handleChange}
                            pHolder="Travel Info"
                            error={fieldStatus('travelInfo').error}
                        />
                    </Field>
                ) : ''}
            </div>
        </SectionCard>
    );
};

export default Amenities;
